import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import type { NotificationListResponseDto } from '../types/notification';
import { listNotifications, markAllNotificationsRead, markNotificationRead } from './notifications.api';

export function useNotifications(enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.notifications(auth.activeCustomerId),
    queryFn: () => listNotifications(20),
    enabled: Boolean(enabled && auth.activeCustomerId),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function useMarkNotificationRead() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const key = queryKeys.notifications(auth.activeCustomerId);
  return useMutation({
    mutationFn: (notificationId: string) => markNotificationRead(notificationId),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<NotificationListResponseDto>(key);
      if (previous) {
        const wasUnread = previous.notifications.some((item) => item.notification_id === notificationId && !item.read_at);
        queryClient.setQueryData<NotificationListResponseDto>(key, {
          ...previous,
          unread_count: wasUnread ? Math.max(0, previous.unread_count - 1) : previous.unread_count,
          notifications: previous.notifications.map((item) => item.notification_id === notificationId
            ? { ...item, read_at: item.read_at ?? new Date().toISOString() }
            : item),
        });
      }
      return { previous };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const key = queryKeys.notifications(auth.activeCustomerId);
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<NotificationListResponseDto>(key);
      if (previous) {
        const now = new Date().toISOString();
        queryClient.setQueryData<NotificationListResponseDto>(key, {
          ...previous,
          unread_count: 0,
          notifications: previous.notifications.map((item) => ({ ...item, read_at: item.read_at ?? now })),
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
